import { useCallback } from 'react'
import axios from 'axios'
import buildParams from '../../helpers/build-params'
import INSURANCE_UNIQUE_ID from '../../shared/constants/Insurances'

let multicalculoProductsRequest = null

const useMultiCalculoClient = () => {
  const getGetPolicyHolders = useCallback(
    (data, noLoading = false) =>
      // eslint-disable-next-line implicit-arrow-linebreak
      axios.get(
        'MultiQuote/multicalculo',
        { params: data, paramsSerializer: buildParams },
        { noLoading },
      ),
    [],
  )

  const getListSolicitation = useCallback(async (data, noLoading = false) => axios.post('/multicalculo/solicitation/list', data, { noLoading })
    .then((response) => (response?.data ? response : { data: { ...response } })), [])

  const registerMulticalculoJudicialAppeal = useCallback(
    async (step1Form) =>
      axios
        .post('/multicalculo/judicial-deposito-recursal', {
          appealDeposit: step1Form,
        })
        .then((response) => (response?.data ? response : { data: { ...response } })),
    [],
  )

  const registerMulticalculoBID = useCallback(
    async (step1Form) =>
      axios
        .post('/multicalculo/bid', {
          bid: step1Form,
        })
        .then((response) => (response?.data ? response : { data: { ...response } })),
    [],
  )

  const registerMulticalculoPerformance = useCallback(
    async (step1Form) =>
      axios
        .post('/multicalculo/performance', {
          performance: step1Form,
        })
        .then((response) => (response?.data ? response : { data: { ...response } })),
    [],
  )

  const getStatusQuotation = useCallback(
    async (identifier) =>
      axios
        .get(`/multicalculo/GetOnPointCotationStatus?identificador=${identifier}`, {})
        .then((response) => response.data),
    [],
  )

  const getQuoteByIdentifier = useCallback(
    async (identifier, noLoading = false) => axios.get('/multicalculo/quote', {
      params: { identifier },
      noLoading,
    }).then((response) => (response?.data ? response : { data: { ...response } })),
    [],
  )

  const downloadDocument = useCallback(
    async ({
      proposalUniqueId, insuranceUniqueId, documentType, base64,
    }) =>
      axios.get('/OnPoint/DownloadDocument', {
        params: {
          ProposalUniqueId: proposalUniqueId,
          InsuranceUniqueId: insuranceUniqueId,
          DocumentType: documentType, // types: Draft, Billet, Policy
          base64: base64 !== false,
        },
      }),
    [],
  )
  const downloadDocumentAvla = useCallback(
    async ({
      proposalUniqueId, documentType, base64, policyUniqueId,
    }) =>
      axios.get('/Avla/DownloadDocument', {
        params: {
          documentId: proposalUniqueId,
          DocumentType: documentType, // types: Draft, Billet, Policy
          base64: base64 !== false,
          PolicyUniqueId: policyUniqueId,
        },
      }),
    [],
  )

  const downloadDocumentByInsurance = useCallback(
    async ({
      proposalUniqueId,
      insuranceUniqueId,
      policyUniqueId,
      documentType,
      base64,
    }) => {
      if (insuranceUniqueId === INSURANCE_UNIQUE_ID.AVLA) {
        return downloadDocumentAvla({
          proposalUniqueId,
          documentType,
          base64,
          policyUniqueId,
        })
      }

      return downloadDocument({
        proposalUniqueId,
        insuranceUniqueId,
        documentType,
        base64,
      })
    },
    [downloadDocument, downloadDocumentAvla],
  )

  const acceptOnPointProposal = useCallback(
    async (identificador, proposalUniqueId, dataPagamentoParcela, numeroParcela) =>
      axios
        .get('/multicalculo/AcceptOnPointProposal', {
          params: {
            Identificador: identificador,
            ProposalUniqueId: proposalUniqueId,
            DataPagamentoParcela: dataPagamentoParcela,
            NumeroParcela: numeroParcela,
          },
        })
        .then((response) => response.data),
    [],
  )

  const updateOnPointFinancialData = useCallback(
    async (identifier, insuranceUniqueId, proposalUniqueId, tax) =>
      axios
        .get('/multicalculo/UpdateProposalFinancialData', {
          params: {
            Identifier: identifier,
            InsuranceUniqueId: insuranceUniqueId,
            ProposalUniqueId: proposalUniqueId,
            Tax: tax,
          },
        })
        .then((response) => response.data),
    [],
  )

  const updateAvlaFinancialData = useCallback(
    async (identifier, proposalUniqueId, tax) =>
      axios
        .get('/multicalculo/UpdateAvlaFinancialData', {
          params: {
            Identifier: identifier,
            ProposalUniqueId: proposalUniqueId,
            Tax: tax,
          },
        })
        .then((response) => response.data),
    [],
  )

  const updateProposalFinancialData = useCallback(
    async (
      identifier,
      insuranceUniqueId,
      proposalUniqueId,
      tax,
    ) => {
      if (insuranceUniqueId === INSURANCE_UNIQUE_ID.AVLA) {
        return updateAvlaFinancialData(
          identifier,
          proposalUniqueId,
          tax,
        )
      }

      return updateOnPointFinancialData(
        identifier,
        insuranceUniqueId,
        proposalUniqueId,
        tax,
      )
    },
    [updateAvlaFinancialData, updateOnPointFinancialData],
  )

  const getMulticalculoProducts = useCallback(
    async (noLoading = false) => {
      if (!multicalculoProductsRequest) {
        multicalculoProductsRequest = axios.get('/MultiQuote/ProductsAndModalities', { noLoading })
          .then((response) => response.data?.data ?? [])
          .catch((error) => {
            multicalculoProductsRequest = null
            throw error
          })
      }

      return multicalculoProductsRequest.catch(() => [])
    },
    [],
  )

  return useCallback(
    () => ({
      getGetPolicyHolders,
      registerMulticalculoJudicialAppeal,
      getStatusQuotation,
      downloadDocument,
      downloadDocumentAvla,
      downloadDocumentByInsurance,
      acceptOnPointProposal,
      getQuoteByIdentifier,
      getListSolicitation,
      updateOnPointFinancialData,
      updateAvlaFinancialData,
      updateProposalFinancialData,
      getMulticalculoProducts,
      registerMulticalculoBID,
      registerMulticalculoPerformance,
    }),
    [
      getGetPolicyHolders,
      registerMulticalculoJudicialAppeal,
      getStatusQuotation,
      downloadDocument,
      downloadDocumentAvla,
      downloadDocumentByInsurance,
      acceptOnPointProposal,
      getQuoteByIdentifier,
      getListSolicitation,
      updateOnPointFinancialData,
      updateAvlaFinancialData,
      updateProposalFinancialData,
      getMulticalculoProducts,
      registerMulticalculoBID,
      registerMulticalculoPerformance,
    ],
  )
}

export default useMultiCalculoClient
